import React, { useRef, useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR from 'swr';
import { Link, useSearchParams } from 'react-router-dom';
import { useRememberFilters } from '@/hooks/useRememberFilters';
import {
  AlertCircle,
  Calendar as CalendarIcon,
  Camera,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  Clock,
  ListTodo,
  Paperclip,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { useCurrentZone } from '@/hooks/useCurrentZone';
import {
  listOpsTasks,
  type OpsTaskListItem,
  type OpsTaskStatus,
} from '@/api/opsTasks';
import { listChecklists, type ChecklistDto, type ChecklistItemDto } from '@/api/ops_tasks';
import type { Paginated } from '@/api/types';
import { useTelegramAutoLogin } from '@/hooks/useTelegramAutoLogin';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useT } from '@/contexts/I18nContext';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { usePageTitle } from '@/contexts/PageHeaderContext';
import { cn } from '@/lib/utils';
import { toAbsoluteUrl } from '@/api/url';
import { API_HOST } from '@/api/base';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { HoverTime, useFrameTime } from '@/components/ui/hover-time';
import { SubmitReportDialog } from '@/components/SubmitReportDialog';

// ── Status visual mapping ─────────────────────────────────────────────────────
// One source of truth for how each status drives the card's accent rail, leading
// token, and badge variant. Statuses: overdue (past deadline), pre_deadline (due
// soon), pending/created (upcoming), done (closed, has report).
type BadgeVariant = 'destructive' | 'warning' | 'success' | 'secondary';

interface StatusMeta {
  /** left accent rail colour */
  rail: string;
  /** leading token colour */
  token: string;
  /** leading icon */
  Icon: React.ComponentType<{ className?: string }>;
  /** status badge variant */
  badge: BadgeVariant;
  labelKey: string;
}

function statusMeta(status: OpsTaskStatus): StatusMeta {
  switch (status) {
    case 'overdue':
      return {
        rail: 'bg-destructive',
        token: 'text-destructive',
        Icon: AlertCircle,
        badge: 'destructive',
        labelKey: 'tasks.statusOverdue',
      };
    case 'pre_deadline':
      return {
        rail: 'bg-warning',
        token: 'text-warning',
        Icon: Clock,
        badge: 'warning',
        labelKey: 'tasks.statusPreDeadline',
      };
    case 'done':
      return {
        rail: 'bg-success',
        token: 'text-success',
        Icon: CheckCircle2,
        badge: 'success',
        labelKey: 'tasks.statusDone',
      };
    default:
      return {
        rail: 'bg-border',
        token: 'text-muted-foreground',
        Icon: Circle,
        badge: 'secondary',
        labelKey: 'tasks.statusPending',
      };
  }
}

function StatusBadge({ status }: { status: OpsTaskStatus }) {
  const { t } = useT();
  const meta = statusMeta(status);
  return <Badge variant={meta.badge}>{t(meta.labelKey)}</Badge>;
}

function todayIso(): string {
  const d = new Date();
  // Local-day ISO; the BE re-anchors to the zone's tz when filtering, so we
  // just need the user's calendar day to seed the picker.
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function shiftIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// Which calendar day is being viewed relative to "today". Drives whether the
// Готово section is collapsed (today, work in progress) or expanded (past day,
// reviewing execution) and whether the day is a non-actionable future plan.
type DateKind = 'past' | 'today' | 'future';
function dateKindOf(iso: string): DateKind {
  const today = todayIso();
  if (iso < today) return 'past';
  if (iso > today) return 'future';
  return 'today';
}

// Ordering weight for active tasks: overdue → due-soon → upcoming.
function activeWeight(status: OpsTaskStatus): number {
  switch (status) {
    case 'overdue':
      return 0;
    case 'pre_deadline':
      return 1;
    default:
      return 2;
  }
}

function dueMillis(task: OpsTaskListItem): number {
  return task.due_at ? new Date(task.due_at).getTime() : Number.POSITIVE_INFINITY;
}

// ── Planned (future-date) projection ──────────────────────────────────────────
// Per-day OpsTask rows are spawned by a daily celery job (task_spawner) only for
// the current local day — so the tasks endpoint returns nothing for future
// dates. To still show "what's planned", we project the already-fetched
// checklist items onto the viewed date using the same scheduling rules the
// spawner uses (_is_task_scheduled_for_today): weekly weekday mask, monthly
// day-of-month, or a one-off specific date.
interface PlannedItem {
  id: number;
  title: string;
  dueTime: string; // HH:MM[:SS]
}

function isScheduledForDate(item: ChecklistItemDto, iso: string): boolean {
  if (!item.active) return false;
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  switch (item.schedule_type) {
    case 'weekly': {
      // Monday-as-0 bit, matching the backend's local_date.weekday() mask.
      const weekdayBit = 1 << ((date.getDay() + 6) % 7);
      return (item.weekdays_mask & weekdayBit) !== 0;
    }
    case 'monthly':
      return item.month_day != null && d === item.month_day;
    case 'once':
      return item.specific_date === iso;
    default:
      return false;
  }
}

function plannedItemsFor(checklists: ChecklistDto[] | undefined, iso: string): PlannedItem[] {
  if (!checklists) return [];
  const out: PlannedItem[] = [];
  for (const cl of checklists) {
    for (const item of cl.items ?? []) {
      if (isScheduledForDate(item, iso)) {
        out.push({ id: item.id, title: item.task_text, dueTime: item.due_time });
      }
    }
  }
  // Order by due time (HH:MM string sort is chronological), then by id for stability.
  return out.sort((a, b) => (a.dueTime < b.dueTime ? -1 : a.dueTime > b.dueTime ? 1 : a.id - b.id));
}

// Render an "HH:MM[:SS]" wall-clock string for the locale, dropping seconds.
function formatPlannedTime(dueTime: string, locale: string): string | null {
  const [h, mn] = dueTime.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(mn)) return null;
  return new Date(2000, 0, 1, h, mn).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function OpsTasksPage() {
  useTelegramAutoLogin();
  const { t, lang } = useT();
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';

  const { zoneId } = useCurrentZone();
  const { user } = useCurrentUser();
  // Дата — в URL (?date=), а не в state: ссылка делится, а уход через сайдбар
  // её не сбрасывает (п. 74). «Сегодня» — по умолчанию, из URL убираем.
  const [params, setParams] = useSearchParams();
  useRememberFilters(['date']);
  const dateRaw = params.get('date');
  const date = dateRaw && /^\d{4}-\d{2}-\d{2}$/.test(dateRaw) ? dateRaw : todayIso();
  const setDate = (upd: string | ((d: string) => string)) => {
    const next = typeof upd === 'function' ? upd(date) : upd;
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next === todayIso()) p.delete('date'); else p.set('date', next);
      return p;
    }, { replace: true });
  };
  const dateKind = dateKindOf(date);
  const isFuture = dateKind === 'future';

  usePageTitle(t('tasks.title'), t('tasks.subtitle'));

  const { data, error, isLoading, mutate } = useSWR<{ results: OpsTaskListItem[] }>(
    zoneId ? ['ops_tasks:list', zoneId, date] : null,
    async () => (await listOpsTasks({ zoneId: zoneId!, date })).data,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  const { data: checklists } = useSWR<Paginated<ChecklistDto>>(
    zoneId ? ['ops_tasks:checklists-status', zoneId] : null,
    async () => (await listChecklists(zoneId!, 100)).data,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  // Gate the date-switcher + "задач нет" UI on ACTUAL task items, not on a bare
  // checklist row. A connected-but-empty checklist (0 task items) is treated as
  // "not configured" → show the configure-checklists note + settings link.
  // A configured checklist with simply 0 tasks *today* still shows the switcher.
  const hasChecklist =
    !!checklists && checklists.results.some(c => (c.items?.length ?? 0) > 0);

  const tasks = data?.results ?? [];
  const apiMissing = !!error && !data;
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const canManage = !!user?.is_superuser;

  if (!zoneId) {
    return <BranchRequiredState />;
  }

  // ── Partition + order ──────────────────────────────────────────────────────
  // Active first (overdue → due-soon → upcoming, then by due time), done at the
  // bottom. Stable & deterministic — equal keys keep their incoming order.
  const active = tasks
    .filter(tk => tk.status !== 'done')
    .map((tk, i) => ({ tk, i }))
    .sort((a, b) => {
      const w = activeWeight(a.tk.status) - activeWeight(b.tk.status);
      if (w !== 0) return w;
      const d = dueMillis(a.tk) - dueMillis(b.tk);
      if (d !== 0) return d;
      return a.i - b.i;
    })
    .map(({ tk }) => tk);
  const done = tasks.filter(tk => tk.status === 'done');

  const total = tasks.length;
  const doneCount = done.length;
  const overdueCount = active.filter(tk => tk.status === 'overdue').length;
  const allDone = total > 0 && doneCount === total;

  // Future dates have no spawned OpsTask rows — project the checklist instead.
  const planned = isFuture ? plannedItemsFor(checklists?.results, date) : [];

  // The done section is *the* content on a past day (reviewing what was done),
  // so it renders expanded; on today it stays collapsed while work is open and
  // auto-opens once everything is closed.
  const doneDefaultOpen = dateKind === 'past' ? true : active.length === 0;

  const showList =
    !isFuture && !isLoading && !apiMissing && hasChecklist && tasks.length > 0;
  const showFuture = isFuture && !apiMissing && hasChecklist;

  return (
    <div className="container mx-auto max-w-4xl space-y-4 px-4 py-6">
      <h1 className="sr-only">{t('tasks.title')}</h1>

      {hasChecklist && (
        <ShiftHeader
          date={date}
          setDate={setDate}
          locale={locale}
          dateKind={dateKind}
          total={total}
          doneCount={doneCount}
          remaining={total - doneCount}
          overdueCount={overdueCount}
          allDone={allDone}
          showStats={showList}
          plannedCount={showFuture ? planned.length : 0}
          showPlanned={showFuture}
        />
      )}

      {!isFuture && isLoading && (
        <Card>
          <CardContent>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="size-4" /> {t('common.loading')}
            </div>
          </CardContent>
        </Card>
      )}

      {apiMissing && (
        <Card>
          <CardContent className="p-0">
            <ServerStatusNotice variant="card" error={error} onRetry={() => mutate()} />
          </CardContent>
        </Card>
      )}

      {!isFuture && !isLoading && !apiMissing && hasChecklist && tasks.length === 0 && (
        <Card>
          <CardContent>
            <div className="flex flex-col items-center gap-2 py-6 text-center">
              <CheckCircle2 className="size-8 text-success" />
              <div className="text-sm font-medium">{t('tasks.empty')}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {showList && (
        <>
          {active.length > 0 && (
            <div className="space-y-2.5">
              {active.map((task, i) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  locale={locale}
                  index={i}
                  onSubmitted={() => mutate()}
                />
              ))}
            </div>
          )}

          {done.length > 0 && (
            <DoneSection
              tasks={done}
              locale={locale}
              defaultOpen={doneDefaultOpen}
              onSubmitted={() => mutate()}
            />
          )}
        </>
      )}

      {/* Future date → non-actionable planned view (projected from the checklist;
          no OpsTask rows exist yet). No "Сдать отчёт" anywhere here. */}
      {showFuture && planned.length > 0 && (
        <div className="space-y-2.5">
          {planned.map((p, i) => (
            <PlannedCard key={p.id} item={p} locale={locale} index={i} />
          ))}
        </div>
      )}

      {showFuture && planned.length === 0 && (
        <Card>
          <CardContent>
            <div className="flex flex-col items-center gap-2 py-6 text-center">
              <CalendarIcon className="size-8 text-muted-foreground" />
              <div className="text-sm font-medium">{t('tasks.plannedEmpty')}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {!isLoading && !apiMissing && !hasChecklist && (
        <Card>
          <CardContent className="space-y-3 py-6 text-center">
            <ListTodo className="mx-auto size-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {canManage ? t('tasks.noChecklistAdmin') : t('tasks.noChecklistWorker')}
            </p>
            {canManage && (
              <Link
                to={{ pathname: '/management/checklists', search }}
                className={cn(buttonVariants({ variant: 'default' }), 'mx-auto inline-flex')}
              >
                {t('dashboard.cards.tasksConfigureChecklists')}
              </Link>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Compact date control ──────────────────────────────────────────────────────
// A tight segmented group: a prev chevron, the DatePicker trigger, a next
// chevron, then "Сегодня" as a reset when off-today. Replaces the wide picker
// flanked by loose ghost buttons — reads as one unit and packs to the right of
// the header bar on desktop.

function DateNav({
  date,
  setDate,
  locale,
}: {
  date: string;
  setDate: React.Dispatch<React.SetStateAction<string>>;
  locale: string;
}) {
  const { t } = useT();
  const offToday = date !== todayIso();
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center rounded-md border border-border bg-card">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => setDate(d => shiftIso(d, -1))}
          aria-label={t('tasks.prevDay')}
          className="rounded-r-none"
        >
          <ChevronLeft className="size-4" />
        </Button>
        <span className="h-5 w-px bg-border" aria-hidden />
        <DatePicker
          value={date}
          onChange={setDate}
          locale={locale}
          ariaLabel={t('tasks.title')}
          className="w-[9.5rem]"
          triggerClassName="rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 hover:bg-muted"
        />
        <span className="h-5 w-px bg-border" aria-hidden />
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => setDate(d => shiftIso(d, 1))}
          aria-label={t('tasks.nextDay')}
          className="rounded-l-none"
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
      {offToday && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDate(todayIso())}
          className="h-9 px-2.5 text-xs"
        >
          {t('tasks.today')}
        </Button>
      )}
    </div>
  );
}

// ── Shift header (progress + date nav, one cohesive bar) ───────────────────────

function ProgressRing({
  done,
  total,
  allDone,
}: {
  done: number;
  total: number;
  allDone: boolean;
}) {
  const r = 20;
  const c = 2 * Math.PI * r;
  const pct = total > 0 ? done / total : 0;
  const offset = c * (1 - pct);
  return (
    <div className="relative size-14 shrink-0">
      <svg viewBox="0 0 48 48" className="size-14 -rotate-90">
        <circle
          cx="24"
          cy="24"
          r={r}
          fill="none"
          strokeWidth="4"
          className="stroke-muted"
        />
        <circle
          cx="24"
          cy="24"
          r={r}
          fill="none"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className={cn(
            'transition-[stroke-dashoffset] duration-500 ease-out',
            allDone ? 'stroke-success' : 'stroke-primary',
          )}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {allDone ? (
          <CheckCircle2 className="size-6 text-success" />
        ) : (
          <span className="text-sm font-semibold tabular-nums">
            {total > 0 ? `${done}/${total}` : '0'}
          </span>
        )}
      </div>
    </div>
  );
}

function ShiftHeader({
  date,
  setDate,
  locale,
  dateKind,
  total,
  doneCount,
  remaining,
  overdueCount,
  allDone,
  showStats,
  plannedCount,
  showPlanned,
}: {
  date: string;
  setDate: React.Dispatch<React.SetStateAction<string>>;
  locale: string;
  dateKind: DateKind;
  total: number;
  doneCount: number;
  remaining: number;
  overdueCount: number;
  allDone: boolean;
  showStats: boolean;
  plannedCount: number;
  showPlanned: boolean;
}) {
  const { t } = useT();
  const isPast = dateKind === 'past';

  // Summary block on the left. For a past day we drop the "осталось/просрочено"
  // urgency framing (the day is over) and just state the completion tally.
  let summary: React.ReactNode = null;
  if (showStats) {
    summary = (
      <div className="flex items-center gap-3.5">
        <ProgressRing done={doneCount} total={total} allDone={allDone} />
        {allDone ? (
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-success">
              <CheckCircle2 className="size-4" />
              {t('tasks.allDone')}
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {isPast ? t('tasks.pastAllDoneHint') : t('tasks.allDoneHint')}
            </div>
          </div>
        ) : (
          <div className="min-w-0 space-y-1.5">
            <div className="text-sm font-semibold tabular-nums">
              {t('tasks.progressDone', { done: doneCount, total })}
            </div>
            {!isPast && (
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge variant="secondary">
                  {t('tasks.remainingCount', { n: remaining })}
                </Badge>
                {overdueCount > 0 && (
                  <Badge variant="destructive">
                    {t('tasks.overdueCount', { n: overdueCount })}
                  </Badge>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  } else if (showPlanned) {
    // Future date: neutral "planned" framing, no overdue pressure.
    summary = (
      <div className="flex items-center gap-3.5">
        <div className="flex size-14 shrink-0 items-center justify-center rounded-full border-4 border-muted text-muted-foreground">
          <CalendarIcon className="size-6" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold">{t('tasks.plannedTitle')}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {t('tasks.plannedCount', { n: plannedCount })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardContent className="py-4">
        {/* Mobile: stack date nav above summary. Desktop (sm+): one row, summary
            left, date nav pinned right. */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          {summary ? (
            <div className="order-2 min-w-0 sm:order-1">{summary}</div>
          ) : (
            <div className="order-2 sm:order-1" />
          )}
          <div className="order-1 shrink-0 sm:order-2">
            <DateNav date={date} setDate={setDate} locale={locale} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Report block (shared by active + done cards) ───────────────────────────────
// Flattened: NOT a nested card. Renders as part of the host card, separated by a
// hairline top border. Keeps reporter line, comment toggle, photo thumbs, video
// links — just without the inner border/background/extra padding.

function ReportBlock({
  report,
  locale,
  compact,
  taskTitle,
}: {
  report: NonNullable<OpsTaskListItem['latest_report']>;
  locale: string;
  compact?: boolean;
  taskTitle?: string;
}) {
  const { t } = useT();
  const frameTime = useFrameTime();
  const [commentExpanded, setCommentExpanded] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  // Keep src + capture time aligned (skip photos with no url in both arrays so
  // the hover chip + lightbox caption line up with the right thumbnail).
  const photos = report.photos.flatMap(p => {
    const u = p.image_url ? toAbsoluteUrl(p.image_url, API_HOST) : null;
    return u ? [{ src: u, time: frameTime(p.created_at) }] : [];
  });
  const photoSrcs: string[] = photos.map(p => p.src);
  const reporter = report.reporter_username
    ? report.reporter_username.replace(/^Менеджеры: |^Вы: /, '')
    : '';
  const captionFor = (i: number) =>
    [taskTitle, reporter, photos[i]?.time].filter(Boolean).join(' · ');

  const thumb = compact ? 'size-12' : 'size-14';

  return (
    <div className={cn('space-y-2 border-t border-border/60', compact ? 'pt-2' : 'pt-2.5')}>
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {report.reporter_username
            ? t('tasks.reporterAt', {
                // Telegram bot stores prefixes like "Менеджеры: " / "Вы: " in reporter_username (hardcoded in bot, always Russian)
                who: report.reporter_username.replace(/^Менеджеры: |^Вы: /, ''),
                time: new Date(report.created_at).toLocaleString(locale, {
                  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                }),
              })
            : new Date(report.created_at).toLocaleString(locale)}
        </span>
      </div>
      {report.text && (
        <div>
          <button
            type="button"
            onClick={() => setCommentExpanded(v => !v)}
            className="text-xs font-medium text-primary hover:underline"
          >
            {commentExpanded ? t('tasks.commentHide') : t('tasks.commentShow')}
          </button>
          {commentExpanded && (
            <p className="mt-1 whitespace-pre-wrap text-sm">{report.text}</p>
          )}
        </div>
      )}
      {(report.photos.length > 0 || report.videos.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {photos.map((p, i) => (
            <button
              key={p.src}
              type="button"
              className={cn(thumb, 'group relative overflow-hidden rounded-md border border-border bg-background')}
              onClick={() => setLightboxIndex(i)}
            >
              <img src={p.src} alt="" className="size-full object-cover" />
              <HoverTime time={p.time} corner="bottom-right" />
            </button>
          ))}
          {lightboxIndex >= 0 && (
            <ImageLightbox
              srcs={photoSrcs}
              index={lightboxIndex}
              alt={taskTitle}
              captions={photoSrcs.map((_, i) => captionFor(i))}
              onClose={() => setLightboxIndex(-1)}
              onNavigate={setLightboxIndex}
            />
          )}
          {report.videos.map(v =>
            v.video_url ? (
              <a
                key={v.id}
                href={v.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(thumb, 'flex items-center justify-center rounded-md border border-border bg-muted text-muted-foreground')}
              >
                <Paperclip className="size-4" />
              </a>
            ) : null,
          )}
        </div>
      )}
    </div>
  );
}

// ── Active task card ──────────────────────────────────────────────────────────

function TaskCard({
  task,
  locale,
  index,
  onSubmitted,
}: {
  task: OpsTaskListItem;
  locale: string;
  index: number;
  onSubmitted: () => void;
}) {
  const { t } = useT();
  const [capturedFiles, setCapturedFiles] = useState<File[] | null>(null);
  const captureRef = useRef<HTMLInputElement>(null);

  const meta = statusMeta(task.status);
  const { Icon } = meta;

  const dueLabel = task.due_at
    ? new Date(task.due_at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    : null;

  const report = task.latest_report;
  const isDone = task.status === 'done';

  return (
    <Card
      className={cn(
        'relative overflow-hidden pl-1 animate-in fade-in slide-in-from-bottom-1 duration-300',
        'transition-shadow sm:hover:shadow-md',
      )}
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms`, animationFillMode: 'backwards' }}
    >
      {/* Left accent rail */}
      <span className={cn('absolute inset-y-0 left-0 w-1', meta.rail)} aria-hidden />
      <CardContent className="space-y-3 py-3.5 pl-4">
        {/* Top row: [token + title/meta] grows left, action pinned right on sm+ */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <Icon className={cn('mt-0.5 size-5 shrink-0', meta.token)} />
            <div className="min-w-0 flex-1 space-y-1.5">
              <span className="block text-sm font-semibold leading-snug break-words">
                {task.title}
              </span>
              {/* Meta row: badge + time chip always in the same place */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <StatusBadge status={task.status} />
                {dueLabel && (
                  <span className={cn('flex items-center gap-1 text-xs', meta.token)}>
                    <Clock className="size-3.5" />
                    {t('tasks.dueAt', { time: dueLabel })}
                  </span>
                )}
              </div>
            </div>
          </div>

          {!isDone && (
            <>
              {/* Single-shot camera: no `multiple` so the OS honours `capture`
                  and opens the camera directly. The report dialog that opens
                  next offers gallery + more-shots. */}
              <input
                ref={captureRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={e => {
                  const files = Array.from(e.target.files ?? []);
                  if (files.length > 0) setCapturedFiles(files);
                  if (captureRef.current) captureRef.current.value = '';
                }}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => captureRef.current?.click()}
                className="w-full shrink-0 gap-1.5 border-primary/30 bg-primary/10 text-primary hover:border-primary/50 hover:bg-primary/15 hover:text-primary sm:w-auto sm:min-w-32"
              >
                <Camera className="size-4" />
                {t('tasks.takePhoto')}
              </Button>
            </>
          )}
        </div>

        {report && <ReportBlock report={report} locale={locale} taskTitle={task.title} />}

        {capturedFiles && (
          <SubmitReportDialog
            task={task}
            initialFiles={capturedFiles}
            onClose={() => setCapturedFiles(null)}
            onSubmitted={() => { setCapturedFiles(null); onSubmitted(); }}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ── Planned (future-date) card ─────────────────────────────────────────────────
// Non-actionable: a neutral "Запланировано" badge, the task text + its scheduled
// time. No "Сдать отчёт" — there's nothing to report against yet.

function PlannedCard({
  item,
  locale,
  index,
}: {
  item: PlannedItem;
  locale: string;
  index: number;
}) {
  const { t } = useT();
  const time = formatPlannedTime(item.dueTime, locale);
  return (
    <Card
      className="relative overflow-hidden pl-1 animate-in fade-in slide-in-from-bottom-1 duration-300"
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms`, animationFillMode: 'backwards' }}
    >
      <span className="absolute inset-y-0 left-0 w-1 bg-border" aria-hidden />
      <CardContent className="py-3.5 pl-4">
        <div className="flex min-w-0 items-start gap-3">
          <CalendarIcon className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <span className="block text-sm font-semibold leading-snug break-words">
              {item.title}
            </span>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <Badge variant="secondary">{t('tasks.planned')}</Badge>
              {time && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="size-3.5" />
                  {t('tasks.dueAt', { time })}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Done section (collapsible, compact, recedes) ──────────────────────────────

function DoneSection({
  tasks,
  locale,
  defaultOpen,
  onSubmitted,
}: {
  tasks: OpsTaskListItem[];
  locale: string;
  defaultOpen: boolean;
  onSubmitted: () => void;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex w-full items-center gap-2 rounded-lg px-1 py-1.5 text-left text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        aria-expanded={open}
      >
        <CheckCircle2 className="size-4 text-success" />
        <span>{t('tasks.doneSection', { n: tasks.length })}</span>
        <ChevronDown
          className={cn('ml-auto size-4 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div className="space-y-2">
          {tasks.map(task => (
            <DoneCard key={task.id} task={task} locale={locale} onSubmitted={onSubmitted} />
          ))}
        </div>
      )}
    </div>
  );
}

function DoneCard({
  task,
  locale,
}: {
  task: OpsTaskListItem;
  locale: string;
  onSubmitted: () => void;
}) {
  const { t } = useT();
  const report = task.latest_report;
  const doneLabel = task.due_at
    ? new Date(task.due_at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <Card className="relative overflow-hidden pl-1 bg-muted/30 animate-in fade-in duration-300">
      <span className="absolute inset-y-0 left-0 w-1 bg-success/60" aria-hidden />
      <CardContent className="space-y-2 py-2.5 pl-4">
        <div className="flex items-center gap-2.5">
          <CheckCircle2 className="size-4 shrink-0 text-success" />
          <span className="min-w-0 flex-1 break-words text-sm font-medium text-muted-foreground">
            {task.title}
          </span>
          {doneLabel && (
            <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
              {t('tasks.dueAt', { time: doneLabel })}
            </span>
          )}
        </div>
        {report && <ReportBlock report={report} locale={locale} compact taskTitle={task.title} />}
      </CardContent>
    </Card>
  );
}
